// src/pages/Settings/ReferencesPage.tsx
//
// Manage the shared referral-source list used by the Reference field on the
// Client and Vendor forms. Anyone who can reach Settings can read the list;
// renaming and deleting need admin or set-references at Manage, matching the
// backend gate in routers/references.py.
import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { referencesApi, DirectoryReference } from '../../api/references';
import { useAuth } from '../../contexts/AuthContext';
import { useAccess } from '../../contexts/AccessContext';
import ConfirmDialog, { ConfirmState } from '../../components/ui/ConfirmDialog';

export default function ReferencesPage() {
  const { user } = useAuth();
  const { hasAccess, canManage: canManageAccess } = useAccess();
  const isAdmin = (user?.role || '').trim().toLowerCase() === 'admin';
  // canManage is permissive for users with no Access profile, so gate on
  // hasAccess too — the same shape CompanyPolicy uses.
  const canManage = isAdmin || (hasAccess && canManageAccess('set-references'));

  const [rows, setRows] = useState<DirectoryReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // A failed read falls back to an empty list silently — referencesApi.list
  // already swallows the error. No "Could not load" toast.
  const load = useCallback(async () => {
    setLoading(true);
    setRows(await referencesApi.list());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { created } = await referencesApi.create(name);
      // Get-or-create: nothing was added when the name was already there, and
      // saying "added" would be a lie.
      toast.success(created ? 'Reference added' : 'That reference already exists');
      setNewName('');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not add the reference');
    }
  };

  const saveRename = async (id: string) => {
    const name = editingName.trim();
    if (!name) return;
    try {
      await referencesApi.rename(id, name);
      toast.success('Reference renamed');
      setEditingId(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not rename the reference');
    }
  };

  const remove = (row: DirectoryReference) => {
    setConfirm({
      title: `Delete "${row.name}"?`,
      description: 'Clients and vendors already using this reference must be changed first.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await referencesApi.remove(row.id);
          toast.success('Reference deleted');
          await load();
        } catch (e: any) {
          // The backend refuses with 409 and a count while the reference is in
          // use — show that message verbatim, it names the number.
          toast.error(e?.response?.data?.detail || 'Could not delete the reference');
        }
        setConfirm(null);
      },
    });
  };

  return (
    <div className="gt-themed space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">References</h2>
        <p className="text-sm text-gray-500">
          Referral sources shown in the Reference dropdown on the Client and Vendor forms.
        </p>
      </div>

      {canManage && (
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="Add a reference…"
            maxLength={150}
            className="gt-input flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={add}
            disabled={!newName.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400">
          No references yet.{canManage ? ' Add one above, or type a new name straight into the Reference field on a client.' : ''}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100 bg-white">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center gap-3 px-4 py-3">
              {editingId === row.id ? (
                <>
                  <input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveRename(row.id); } }}
                    maxLength={150}
                    autoFocus
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                  />
                  <button type="button" aria-label="Save name" onClick={() => saveRename(row.id)}
                          className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50">
                    <Check className="h-4 w-4" />
                  </button>
                  <button type="button" aria-label="Cancel rename" onClick={() => setEditingId(null)}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100">
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 truncate text-sm text-gray-900">{row.name}</span>
                  {canManage && (
                    <>
                      <button type="button" aria-label={`Rename ${row.name}`}
                              onClick={() => { setEditingId(row.id); setEditingName(row.name); }}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-blue-600">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button type="button" aria-label={`Delete ${row.name}`}
                              onClick={() => remove(row)}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
